// Centralized claim status constants and utilities

// Define the claim status type based on database schema
export type ClaimStatus = 
  | 'Acknowledgment' 
  | 'Documents' 
  | 'Details' 
  | 'Contract Review' 
  | 'Quantity Review' 
  | 'Calculation Review' 
  | 'Final Decision' 
  | 'Approved' 
  | 'Rejected' 
  | 'Withdrawn';

// Define claim types for categorizing different kinds of claims
export type ClaimType = 
  | 'Fixed Price'
  | 'Time & Materials';

// Canonical status sequence defining the natural progression order
// This is used for determining stage completion, direction of changes, etc.
export const CLAIM_STATUS_SEQUENCE: ClaimStatus[] = [
  'Acknowledgment', 
  'Documents',
  'Details',
  'Contract Review',
  'Quantity Review', 
  'Calculation Review',
  'Final Decision',
  'Approved',
  'Rejected',
  'Withdrawn'
];

// Terminal states that end the workflow
export const TERMINAL_STATUSES: ClaimStatus[] = ['Approved', 'Rejected', 'Withdrawn'];

// Utility functions
export function getStatusDisplayName(status: ClaimStatus): string {
  // Since database values now match display values, just return the status
  return status;
}

export function getStatusFromDisplayName(displayName: string): ClaimStatus | null {
  // Check if the display name is a valid status
  return CLAIM_STATUS_SEQUENCE.includes(displayName as ClaimStatus) ? displayName as ClaimStatus : null;
}

export function isTerminalStatus(status: ClaimStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

export function getStatusIndex(status: ClaimStatus): number {
  const index = CLAIM_STATUS_SEQUENCE.indexOf(status);
  return index === -1 ? -1 : index;
}

export function isStatusAfter(status: ClaimStatus, compareStatus: ClaimStatus): boolean {
  const statusIndex = getStatusIndex(status);
  const compareIndex = getStatusIndex(compareStatus);
  
  // Handle terminal states specially
  if (isTerminalStatus(status) || isTerminalStatus(compareStatus)) {
    return false;
  }
  
  return statusIndex > compareIndex && statusIndex !== -1 && compareIndex !== -1;
}

export function isStatusBefore(status: ClaimStatus, compareStatus: ClaimStatus): boolean {
  const statusIndex = getStatusIndex(status);
  const compareIndex = getStatusIndex(compareStatus);
  
  // Handle terminal states specially  
  if (isTerminalStatus(status) || isTerminalStatus(compareStatus)) {
    return false;
  }
  
  return statusIndex < compareIndex && statusIndex !== -1 && compareIndex !== -1;
} 